import React, { useState } from 'react';
import axios from 'axios';
import './index.css';
import { POST_REVENUE_DATA } from '../../api/revenue';
import {
    validateRequired,
    validateEmail,
    validateUrl,
    validatePhone,
    validateSelect,
    validateBoolean,
} from '../../utils/validators';

const steps = [
    { id: 1, label: 'Company' },
    { id: 2, label: 'Traction' },
    { id: 3, label: 'Growth brief' },
    { id: 4, label: 'Partnership' },
];

const supportOptions = [
    'GTM strategy',
    'Sales execution',
    'Partnerships',
    'Founder network',
    'Market expansion',
    'Specialist talent',
];

const initialFormData = {
    fullName: '',
    workEmail: '',
    companyName: '',
    website: '',
    cityCountry: '',
    phone: '',
    sector: '',
    stage: '',
    monthlyRevenue: '',
    teamSize: '',
    marketsServed: '',
    growthPriority: '',
    incrementalTarget: '',
    ninetyDaysGoal: '',
    selectedSupport: [],
    urgencyReason: '',
    agreedToTerms: false,
};

const RevenueExchangeForm = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    const validateStep = (step) => {
        const errors = {};

        if (step === 1) {
            const nameErr = validateRequired(formData.fullName, 'Full name', 2);
            if (nameErr) errors.fullName = nameErr;

            const emailErr = validateEmail(formData.workEmail, 'Work email', true);
            if (emailErr) errors.workEmail = emailErr;

            const companyErr = validateRequired(formData.companyName, 'Company name', 2);
            if (companyErr) errors.companyName = companyErr;

            if (formData.website && formData.website.trim()) {
                const urlErr = validateUrl(formData.website, 'Website URL', false);
                if (urlErr) errors.website = urlErr;
            }

            const cityErr = validateRequired(formData.cityCountry, 'City & country', 2);
            if (cityErr) errors.cityCountry = cityErr;

            if (formData.phone && formData.phone.trim()) {
                const phoneErr = validatePhone(formData.phone, 'Phone number', false);
                if (phoneErr) errors.phone = phoneErr;
            }
        } else if (step === 2) {
            const sectorErr = validateSelect(formData.sector, 'your sector');
            if (sectorErr) errors.sector = sectorErr;

            const stageErr = validateSelect(formData.stage, 'your company stage');
            if (stageErr) errors.stage = stageErr;

            const revErr = validateSelect(formData.monthlyRevenue, 'your current monthly revenue range');
            if (revErr) errors.monthlyRevenue = revErr;

            const teamErr = validateSelect(formData.teamSize, 'your team size');
            if (teamErr) errors.teamSize = teamErr;
        } else if (step === 3) {
            const growthErr = validateSelect(formData.growthPriority, 'a primary growth priority');
            if (growthErr) errors.growthPriority = growthErr;

            const targetErr = validateSelect(formData.incrementalTarget, 'a 12-month incremental target');
            if (targetErr) errors.incrementalTarget = targetErr;

            const goalErr = validateRequired(formData.ninetyDaysGoal, '90 days goal description', 5);
            if (goalErr) errors.ninetyDaysGoal = 'Please describe what must be true 90 days from now (minimum 5 characters).';
        } else if (step === 4) {
            const urgencyErr = validateRequired(formData.urgencyReason, 'Urgency reason', 5);
            if (urgencyErr) errors.urgencyReason = 'Please explain why now is the right time (minimum 5 characters).';

            const termsErr = validateBoolean(formData.agreedToTerms, 'Please agree to the terms before submitting your brief.');
            if (termsErr) errors.agreedToTerms = termsErr;
        }

        return errors;
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
        if (errorMsg) setErrorMsg('');
    };

    const toggleSupportOption = (option) => {
        setFormData((prev) => {
            const exists = prev.selectedSupport.includes(option);
            return {
                ...prev,
                selectedSupport: exists
                    ? prev.selectedSupport.filter((item) => item !== option)
                    : [...prev.selectedSupport, option],
            };
        });
        if (errorMsg) setErrorMsg('');
    };

    const handleNext = async (e) => {
        if (e) e.preventDefault();
        
        const errors = validateStep(currentStep);
        if (Object.keys(errors).length > 0) {
            const firstMsg = Object.values(errors)[0];
            setErrorMsg(firstMsg);
            setFieldErrors(errors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setFieldErrors({});
        setErrorMsg('');

        if (currentStep < 4) {
            setCurrentStep((prev) => prev + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Final step submission
        if (!formData.agreedToTerms) {
            setErrorMsg('Please agree to the terms before submitting your brief.');
            setFieldErrors({ agreedToTerms: 'Agreement to terms is required' });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(POST_REVENUE_DATA, formData);
            if (response.status === 201 || response.status === 200) {
                setSubmitted(true);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } catch (error) {
            console.error('Error submitting revenue brief:', error);
            if (error.response && error.response.data) {
                const details = error.response.data.details;
                if (Array.isArray(details)) {
                    setErrorMsg(details.join('. '));
                } else if (typeof details === 'string') {
                    setErrorMsg(details);
                } else if (error.response.data.error) {
                    setErrorMsg(error.response.data.error);
                } else {
                    setErrorMsg('An error occurred while submitting your brief. Please try again.');
                }
            } else {
                setErrorMsg('Network error. Please check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep((prev) => prev - 1);
            setErrorMsg('');
            setFieldErrors({});
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setCurrentStep(1);
        setSubmitted(false);
        setErrorMsg('');
        setFieldErrors({});
    };

    const progressPercent = (currentStep / steps.length) * 100;

    return (
        <div className="ref-page-layout">
            <div className="ref-container">
                {/* Left Dark Sidebar */}
                <aside className="ref-dark-sidebar">
                    <div className="ref-sidebar-radar-bg">
                        <div className="ref-radar-ring r-1"></div>
                        <div className="ref-radar-ring r-2"></div>
                        <div className="ref-radar-ring r-3"></div>
                    </div>

                    <div className="ref-sidebar-content">
                        <div className="ref-sidebar-top">
                            <div className="ref-brand-header">
                                <span className="ref-brand-name">MARKOKNOW</span>
                                <span className="ref-brand-badge">BUILD WITH US</span>
                            </div>

                            <div className="ref-pill-category">REVENUE EXCHANGE</div>
                            <h1 className="ref-sidebar-title">
                                Build the next<br />
                                revenue line,<br />
                                <em>together.</em>
                            </h1>
                            <p className="ref-sidebar-desc">
                                Tell us where you are, where growth is stuck, and what a
                                meaningful next 90 days looks like.
                            </p>
                        </div>

                        <div className="ref-sidebar-features">
                            <div className="ref-feature-item">
                                <div className="ref-feature-icon">✦</div>
                                <div>
                                    <strong>Aligned upside</strong>
                                    <p>We earn from the incremental revenue we help create.</p>
                                </div>
                            </div>

                            <div className="ref-feature-item">
                                <div className="ref-feature-icon">🛡️</div>
                                <div>
                                    <strong>Founder controlled</strong>
                                    <p>Your baseline and engagement terms are agreed before work begins.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Right Form Container */}
                <section className="ref-form-wrapper">
                    <div className="ref-card-container">
                        {submitted ? (
                            <div className="ref-success-view">
                                <div className="ref-success-icon-badge">✓</div>
                                <h2 className="ref-success-title">Growth Brief Received!</h2>
                                <p className="ref-success-subtitle">
                                    Thank you, <strong>{formData.fullName}</strong>. We've received the brief for <strong>{formData.companyName}</strong>.
                                </p>
                                <div className="ref-success-details-card">
                                    <div className="ref-success-detail-row">
                                        <span className="ref-success-detail-label">Work Email</span>
                                        <span className="ref-success-detail-value">{formData.workEmail}</span>
                                    </div>
                                    <div className="ref-success-detail-row">
                                        <span className="ref-success-detail-label">Sector & Stage</span>
                                        <span className="ref-success-detail-value">{formData.sector} • {formData.stage}</span>
                                    </div>
                                    <div className="ref-success-detail-row">
                                        <span className="ref-success-detail-label">Growth Priority</span>
                                        <span className="ref-success-detail-value">{formData.growthPriority}</span>
                                    </div>
                                    <div className="ref-success-detail-row">
                                        <span className="ref-success-detail-label">12M Target</span>
                                        <span className="ref-success-detail-value">{formData.incrementalTarget}</span>
                                    </div>
                                </div>
                                <p className="ref-success-next-step">
                                    Our operators will review your growth bottlenecks and reach out within 2-3 business days if there is strong alignment.
                                </p>
                                <div className="ref-success-actions">
                                    <button
                                        type="button"
                                        className="ref-btn-continue submit-mode"
                                        onClick={handleReset}
                                    >
                                        Submit Another Brief
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Attached Stepper and Progress Bar */}
                                <div className="ref-card-header-stepper">
                                    <div className="ref-stepper-row">
                                        <div className="ref-stepper-items">
                                            {steps.map((step) => (
                                                <div
                                                    key={step.id}
                                                    className={`ref-stepper-item ${currentStep === step.id
                                                            ? 'active'
                                                            : currentStep > step.id
                                                                ? 'completed'
                                                                : ''
                                                        }`}
                                                    onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                                                >
                                                    {step.label}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="ref-estimate-time">Takes about 4 minutes</div>
                                    </div>

                                    <div className="ref-progress-indicator-track">
                                        <div
                                            className="ref-progress-indicator-fill"
                                            style={{ width: `${progressPercent}%` }}
                                        ></div>
                                    </div>
                                </div>

                                {/* Form Body Card */}
                                <div className="ref-form-card-inner">
                                    {errorMsg && (
                                        <div className="ref-error-banner">
                                            <span className="ref-error-icon">⚠️</span>
                                            <span>{errorMsg}</span>
                                        </div>
                                    )}

                                    <form onSubmit={handleNext}>
                                        {/* STEP 1: COMPANY */}
                                        {currentStep === 1 && (
                                            <div className="ref-step-content">
                                                <div className="ref-step-tag">YOUR COMPANY</div>
                                                <h2 className="ref-step-heading">First, tell us who is building.</h2>
                                                <p className="ref-step-caption">
                                                    We use this to understand the business and make sure the right operator reviews it.
                                                </p>

                                                <div className="ref-form-grid">
                                                    <div className="ref-field-group">
                                                        <label>Your name</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Founder or lead"
                                                            value={formData.fullName}
                                                            className={fieldErrors.fullName ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('fullName', e.target.value)}
                                                            required
                                                        />
                                                        {fieldErrors.fullName && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.fullName}</span>
                                                        )}
                                                    </div>
                                                    <div className="ref-field-group">
                                                        <label>Work email</label>
                                                        <input
                                                            type="email"
                                                            placeholder="you@company.com"
                                                            value={formData.workEmail}
                                                            className={fieldErrors.workEmail ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('workEmail', e.target.value)}
                                                            required
                                                        />
                                                        {fieldErrors.workEmail && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.workEmail}</span>
                                                        )}
                                                    </div>
                                                    <div className="ref-field-group">
                                                        <label>Company name</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Your company"
                                                            value={formData.companyName}
                                                            className={fieldErrors.companyName ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('companyName', e.target.value)}
                                                            required
                                                        />
                                                        {fieldErrors.companyName && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.companyName}</span>
                                                        )}
                                                    </div>
                                                    <div className="ref-field-group">
                                                        <label>Website (optional)</label>
                                                        <input
                                                            type="text"
                                                            placeholder="company.com"
                                                            value={formData.website}
                                                            className={fieldErrors.website ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('website', e.target.value)}
                                                        />
                                                        {fieldErrors.website && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.website}</span>
                                                        )}
                                                    </div>
                                                    <div className="ref-field-group">
                                                        <label>City & country</label>
                                                        <input
                                                            type="text"
                                                            placeholder="Bengaluru, India"
                                                            value={formData.cityCountry}
                                                            className={fieldErrors.cityCountry ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('cityCountry', e.target.value)}
                                                            required
                                                        />
                                                        {fieldErrors.cityCountry && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.cityCountry}</span>
                                                        )}
                                                    </div>
                                                    <div className="ref-field-group">
                                                        <label>Phone (optional)</label>
                                                        <input
                                                            type="tel"
                                                            placeholder="+91 98765 43210"
                                                            value={formData.phone}
                                                            className={fieldErrors.phone ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('phone', e.target.value)}
                                                        />
                                                        {fieldErrors.phone && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.phone}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* STEP 2: TRACTION */}
                                        {currentStep === 2 && (
                                            <div className="ref-step-content">
                                                <div className="ref-step-tag">CURRENT TRACTION</div>
                                                <h2 className="ref-step-heading">Show us the traction already in motion.</h2>
                                                <p className="ref-step-caption">
                                                     Ranges are enough. We are looking for evidence, clarity and a growth problem we can materially influence.
                                                </p>

                                                <div className="ref-form-grid">
                                                    <div className="ref-field-group">
                                                        <label>Sector</label>
                                                        <select
                                                            value={formData.sector}
                                                            className={fieldErrors.sector ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('sector', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select sector</option>
                                                            <option value="B2B SaaS">B2B SaaS</option>
                                                            <option value="Fintech">Fintech</option>
                                                            <option value="Consumer / D2C">Consumer / D2C</option>
                                                            <option value="Enterprise Tech">Enterprise Tech</option>
                                                            <option value="Healthcare / HealthTech">Healthcare / HealthTech</option>
                                                            <option value="EdTech">EdTech</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                        {fieldErrors.sector && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.sector}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group">
                                                        <label>Stage</label>
                                                        <select
                                                            value={formData.stage}
                                                            className={fieldErrors.stage ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('stage', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select stage</option>
                                                            <option value="Pre-seed">Pre-seed</option>
                                                            <option value="Seed">Seed</option>
                                                            <option value="Series A">Series A</option>
                                                            <option value="Series B+">Series B+</option>
                                                            <option value="Bootstrapped">Bootstrapped</option>
                                                        </select>
                                                        {fieldErrors.stage && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.stage}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group">
                                                        <label>Current monthly revenue</label>
                                                        <select
                                                            value={formData.monthlyRevenue}
                                                            className={fieldErrors.monthlyRevenue ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('monthlyRevenue', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select range (USD)</option>
                                                            <option value="Pre-revenue">Pre-revenue</option>
                                                            <option value="$1k - $10k">$1,000 – $10,000</option>
                                                            <option value="$10k - $50k">$10,000 – $50,000</option>
                                                            <option value="$50k - $100k">$50,000 – $100,000</option>
                                                            <option value="$100k+">$100,000+</option>
                                                        </select>
                                                        {fieldErrors.monthlyRevenue && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.monthlyRevenue}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group">
                                                        <label>Team size</label>
                                                        <select
                                                            value={formData.teamSize}
                                                            className={fieldErrors.teamSize ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('teamSize', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select team size</option>
                                                            <option value="1-5">1–5</option>
                                                            <option value="6-15">6–15</option>
                                                            <option value="16-50">16–50</option>
                                                            <option value="50+">50+</option>
                                                        </select>
                                                        {fieldErrors.teamSize && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.teamSize}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group full-width">
                                                        <label>Markets you currently serve (optional)</label>
                                                        <input
                                                            type="text"
                                                            placeholder="e.g. India and Singapore; mid-market SaaS teams"
                                                            value={formData.marketsServed}
                                                            onChange={(e) => handleChange('marketsServed', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* STEP 3: GROWTH BRIEF */}
                                        {currentStep === 3 && (
                                            <div className="ref-step-content">
                                                <div className="ref-step-tag">GROWTH BRIEF</div>
                                                <h2 className="ref-step-heading">Define the revenue we could unlock.</h2>
                                                <p className="ref-step-caption">
                                                    Choose the single priority that would change the trajectory—not a list of everything that could improve.
                                                </p>

                                                <div className="ref-form-grid">
                                                    <div className="ref-field-group">
                                                        <label>Primary growth priority</label>
                                                        <select
                                                            value={formData.growthPriority}
                                                            className={fieldErrors.growthPriority ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('growthPriority', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select one priority</option>
                                                            <option value="Enterprise Sales">Enterprise Sales Conversion</option>
                                                            <option value="Channel Partnerships">Channel & Partner Network</option>
                                                            <option value="International Expansion">International Market Entry</option>
                                                            <option value="Inbound Funnel">Inbound Demand Generation</option>
                                                            <option value="Pricing & Packaging">Pricing & Packaging Modernization</option>
                                                        </select>
                                                        {fieldErrors.growthPriority && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.growthPriority}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group">
                                                        <label>12-month incremental target</label>
                                                        <select
                                                            value={formData.incrementalTarget}
                                                            className={fieldErrors.incrementalTarget ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('incrementalTarget', e.target.value)}
                                                            required
                                                        >
                                                            <option value="">Select target (INR)</option>
                                                            <option value="₹25L - ₹50L">₹25 Lakhs – ₹50 Lakhs</option>
                                                            <option value="₹50L - ₹1 Cr">₹50 Lakhs – ₹1 Crore</option>
                                                            <option value="₹1 Cr - ₹3 Cr">₹1 Crore – ₹3 Crores</option>
                                                            <option value="₹3 Cr - ₹5 Cr">₹3 Crores – ₹5 Crores</option>
                                                            <option value="₹5 Cr - ₹10 Cr">₹5 Crores – ₹10 Crores</option>
                                                            <option value="₹10 Cr+">₹10 Crores+</option>
                                                        </select>
                                                        {fieldErrors.incrementalTarget && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.incrementalTarget}</span>
                                                        )}
                                                    </div>

                                                    <div className="ref-field-group full-width">
                                                        <label>What must be true 90 days from now?</label>
                                                        <textarea
                                                            rows={5}
                                                            maxLength={1000}
                                                            placeholder="Describe the measurable result, current obstacle and why it matters now."
                                                            value={formData.ninetyDaysGoal}
                                                            className={fieldErrors.ninetyDaysGoal ? 'ref-input-error' : ''}
                                                            onChange={(e) => handleChange('ninetyDaysGoal', e.target.value)}
                                                            required
                                                        ></textarea>
                                                        {fieldErrors.ninetyDaysGoal && (
                                                            <span className="ref-field-error-msg">⚠️ {fieldErrors.ninetyDaysGoal}</span>
                                                        )}
                                                        <div className="ref-char-counter">
                                                            {formData.ninetyDaysGoal.length}/1000
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* STEP 4: PARTNERSHIP FIT */}
                                        {currentStep === 4 && (
                                            <div className="ref-step-content">
                                                <div className="ref-step-tag">PARTNERSHIP FIT</div>
                                                <h2 className="ref-step-heading">What would make us useful?</h2>
                                                <p className="ref-step-caption">
                                                    Select the capabilities that matter. If the fit is strong, we will propose a scoped engagement with a documented baseline.
                                                </p>

                                                <div className="ref-field-group full-width">
                                                    <label>Support needed (select all that apply)</label>
                                                    <div className="ref-options-pill-grid">
                                                        {supportOptions.map((opt) => {
                                                            const isSelected = formData.selectedSupport.includes(opt);
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={opt}
                                                                    className={`ref-select-pill ${isSelected ? 'selected' : ''}`}
                                                                    onClick={() => toggleSupportOption(opt)}
                                                                >
                                                                    {isSelected ? '✓ ' : '+ '}{opt}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="ref-field-group full-width" style={{ marginTop: '20px' }}>
                                                    <label>Why is now the right time?</label>
                                                    <textarea
                                                        rows={4}
                                                        placeholder="Share the trigger, urgency or window of opportunity."
                                                        value={formData.urgencyReason}
                                                        className={fieldErrors.urgencyReason ? 'ref-input-error' : ''}
                                                        onChange={(e) => handleChange('urgencyReason', e.target.value)}
                                                        required
                                                    ></textarea>
                                                    {fieldErrors.urgencyReason && (
                                                        <span className="ref-field-error-msg">⚠️ {fieldErrors.urgencyReason}</span>
                                                    )}
                                                </div>

                                                <div className="ref-terms-agreement">
                                                    <label className="ref-checkbox-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.agreedToTerms}
                                                            onChange={(e) => handleChange('agreedToTerms', e.target.checked)}
                                                            required
                                                        />
                                                        <span>
                                                            I understand this is an application, not an offer. Any revenue-share percentage, baseline, scope and term will be agreed in writing before work begins.
                                                        </span>
                                                    </label>
                                                    {fieldErrors.agreedToTerms && (
                                                        <span className="ref-field-error-msg" style={{ marginTop: '8px' }}>⚠️ {fieldErrors.agreedToTerms}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Bottom Navigation Actions */}
                                        <div className="ref-actions-row">
                                            <button
                                                type="button"
                                                className={`ref-btn-back ${currentStep === 1 || loading ? 'disabled' : ''}`}
                                                onClick={handleBack}
                                                disabled={currentStep === 1 || loading}
                                            >
                                                ← Back
                                            </button>

                                            <div className="ref-step-meta-indicator">
                                                Step {currentStep} of {steps.length}
                                            </div>

                                            <button
                                                type="submit"
                                                className={`ref-btn-continue ${currentStep === 4 ? 'submit-mode' : ''} ${loading ? 'loading' : ''}`}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <span>Submitting brief...</span>
                                                ) : currentStep === 4 ? (
                                                    <>Submit growth brief 🚀</>
                                                ) : (
                                                    <>Continue →</>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </>
                        )}
                    </div>

                    <footer className="ref-disclaimer">
                        Your information is used only to assess partnership fit. We do not sell application data or make introductions without your approval.
                    </footer>
                </section>
            </div>
        </div>
    );
};

export default RevenueExchangeForm;